import { Type } from '@scalar/typebox';
import { compose } from '../../../schemas/compose.js';
import { XScalarIgnoreSchema } from '../../../schemas/extensions/document/x-scalar-ignore.js';
import { XDefaultScopesSchema } from '../../../schemas/extensions/security/x-default-scopes.js';
import { OAuthFlowsObjectRef } from '../../../schemas/v3.2/strict/ref-definitions.js';
// Shared base for every security scheme: a description plus the ignore extension, so any
// scheme can be hidden from the auth UI with `x-scalar-ignore`. See documentation/openapi.md.
const DescriptionSchema = compose(Type.Object({
    /** A description for security scheme. CommonMark syntax MAY be used for rich text representation. */
    description: Type.Optional(Type.String()),
    /** Declares this security scheme to be deprecated. Consumers SHOULD refrain from usage of the declared scheme. Added in OpenAPI 3.2. */
    deprecated: Type.Optional(Type.Boolean()),
}), XScalarIgnoreSchema);
const ApiKeySchema = compose(DescriptionSchema, Type.Object({
    /** REQUIRED. The type of the security scheme. Valid values are "apiKey", "http", "mutualTLS", "oauth2", "openIdConnect". */
    type: Type.Literal('apiKey'),
    /** REQUIRED. The name of the header, query or cookie parameter to be used. */
    name: Type.String(),
    /** REQUIRED. The location of the API key. Valid values are "query", "header", or "cookie". */
    in: Type.Union([Type.Literal('query'), Type.Literal('header'), Type.Literal('cookie')]),
}));
const HttpSchema = compose(DescriptionSchema, Type.Object({
    /** REQUIRED. The type of the security scheme. Valid values are "apiKey", "http", "mutualTLS", "oauth2", "openIdConnect". */
    type: Type.Literal('http'),
    /** REQUIRED. The name of the HTTP Authentication scheme to be used in the Authorization header as defined in RFC7235. The values used SHOULD be registered in the IANA Authentication Scheme registry. The value is case-insensitive, as defined in RFC7235. */
    scheme: Type.Union([Type.Literal('basic'), Type.Literal('bearer')]),
    /** A hint to the client to identify how the bearer token is formatted. Bearer tokens are usually generated by an authorization server, so this information is primarily for documentation purposes. */
    bearerFormat: Type.Optional(Type.String()),
}));
const MutualTlsSchema = compose(DescriptionSchema, Type.Object({
    /** REQUIRED. The type of the security scheme. Valid values are "apiKey", "http", "mutualTLS", "oauth2", "openIdConnect". */
    type: Type.Literal('mutualTLS'),
}));
const OAuth2 = compose(DescriptionSchema, Type.Object({
    /** REQUIRED. The type of the security scheme. Valid values are "apiKey", "http", "mutualTLS", "oauth2", "openIdConnect". */
    type: Type.Literal('oauth2'),
    /** REQUIRED. An object containing configuration information for the flow types supported. */
    flows: OAuthFlowsObjectRef,
    /** URL to the OAuth2 authorization server metadata (RFC8414). Use HTTPS, or HTTP for local development URLs. Added in OpenAPI 3.2. */
    oauth2MetadataUrl: Type.Optional(Type.String()),
}), XDefaultScopesSchema);
const OpenIdConnect = compose(DescriptionSchema, Type.Object({
    /** REQUIRED. The type of the security scheme. Valid values are "apiKey", "http", "mutualTLS", "oauth2", "openIdConnect". */
    type: Type.Literal('openIdConnect'),
    /** REQUIRED. Well-known URL to discover the [[OpenID-Connect-Discovery]] provider metadata. */
    openIdConnectUrl: Type.String(),
}));
/**
 * Defines a security scheme that can be used by the operations.
 *
 * Supported schemes are HTTP authentication, an API key (either as a header, a cookie parameter or as a query parameter), mutual TLS (use of a client certificate), OAuth2's common flows (implicit, password, client credentials and authorization code) as defined in RFC6749, and [[OpenID-Connect-Core]]. Please note that as of 2020, the implicit flow is about to be deprecated by OAuth 2.0 Security Best Current Practice. Recommended for most use cases is Authorization Code Grant flow with PKCE.
 */
export const SecuritySchemeObjectSchemaDefinition = Type.Union([
    ApiKeySchema,
    HttpSchema,
    MutualTlsSchema,
    OAuth2,
    OpenIdConnect,
]);
